/**
 * AIO-82885 — the language + country the Skynet chat widget is given, and moving a running
 * widget to a new pair. Shared by `projects/desktop` and `projects/mobile`, which are
 * otherwise separate source trees (see `projects/csr-routes.ts` for the same pattern).
 *
 * ── WHY NOT destroy() + init() ───────────────────────────────────────────
 *
 * Measured against the vendor's widget.js: init() returns at once when the widget is mounted
 * OR has been destroyed, and destroy() sets that destroyed flag for good. So a destroyed
 * `$chatWidget` can never be started again — calling init() on it leaves the page with no
 * chat at all. What the bundle does offer:
 *
 *   · `setLanguage(code)` — the widget's own in-place switch: re-renders, keeps the socket
 *     and the transcript, and saves the code in the widget's localStorage.
 *   · `CustomerChatWidgetBundle.createCustomerChatWidgetSdk()` — a NEW instance (every piece
 *     of widget state lives inside it). Exported but undocumented, so it is used only for a
 *     country change, which init() is the only way to hand over.
 *
 * The open chat survives a re-mount: the session ticket sits in the widget's own localStorage
 * under the embed key, destroy() leaves it there, and the new instance's init() resumes with
 * it. reset() and clear() are NEVER called here — reset() is logout (it drops the ticket),
 * clear() starts a new conversation.
 *
 * Browser-only; the caller guards. No timers — see the NgZone note on SkynetService.identify.
 *
 * ── WHERE ITS TESTS LIVE ─────────────────────────────────────────────────
 *
 * projects/desktop/src/app/core/services/skynet-locale.spec.ts, not beside this file. The
 * karma builder globs specs relative to a PROJECT root (projects/desktop, projects/mobile),
 * so nothing under projects/ itself is ever collected however tsconfig.spec.json is written —
 * measured, not assumed. The suite is deliberately run once, under desktop-qp2a.
 */

/** What Skynet is told about the page, besides its embed key and endpoint. */
export interface SkynetLocale {
  language: string;
  /** Left out when the url has no country, so the settings are exactly what they were before. */
  country?: string;
}

export interface SkynetSettings extends SkynetLocale {
  embed_key: string;
  endpoint: string;
}

/** The wrapper script that loads widget.js. Same id in QPRO, template-02 and template-ug. */
export const SKYNET_SCRIPT_ID = 'skynet';

/** The script that attaches the signed-in member. Removed on logout and before a re-mount. */
export const SKYNET_SET_USER_SCRIPT_ID = 'skynetSetUser';

/**
 * Skynet's seven languages, as its own picker lists them (AIO-84471; AIO-81902 had the three
 * of guide v1.7 §5). QPLY ships `en hk id ms th zh` — `vi` and `km` are listed for the day a
 * brand adds them, and cost nothing until then.
 */
const LANGUAGES = ['en', 'zh', 'ms', 'id', 'th', 'vi', 'km'];

/**
 * QPLY serves `hk` (traditional Chinese), which Skynet has no code for — its picker offers a
 * single 中文 with no traditional/simplified split. Send that rather than let an HK reader
 * fall through to English (AIO-84471).
 */
const ALIASES: Record<string, string> = { hk: 'zh' };

interface ChatWidget {
  init?: (settings: SkynetSettings) => unknown;
  setLanguage?: (language: string) => unknown;
  destroy?: () => unknown;
}

interface SkynetWindow {
  $chatWidget?: ChatWidget;
  CustomerChatWidget?: ChatWidget;
  $chatWidgetSettings?: SkynetSettings;
  CustomerChatWidgetBundle?: { createCustomerChatWidgetSdk?: () => ChatWidget };
}

/**
 * The pair for a page locale's two halves.
 *
 * ⚠️ DomainService.getLanguageCode() and getCountryCode() answer in UPPERCASE ('EN', 'MY') —
 * domain.service.ts:186-207. QPRO's copy of this function takes lowercase input and would
 * answer `{language: 'en'}` for every QPLY brand, with no country at all. Lowercase here, so
 * the caller cannot get it wrong.
 */
export function skynetLocale(languageCode: string, countryCode: string): SkynetLocale {
  const code = (languageCode ?? '').toLowerCase();
  const language = ALIASES[code] ?? code;
  const country = (countryCode ?? '').toLowerCase();

  return {
    language: LANGUAGES.includes(language) ? language : 'en',
    ...(/^[a-z]{2}$/.test(country) ? { country } : {}),
  };
}

export function isSameSkynetLocale(a: SkynetLocale, b: SkynetLocale): boolean {
  return a?.language === b?.language && a?.country === b?.country;
}

/**
 * Move the widget to `settings`. Answers TRUE only when a new instance was started and
 * mounted in place of the old one, which is the caller's cue to attach the signed-in member
 * again — the new instance starts anonymous. FALSE means the running widget was kept (its
 * language switched), and nothing needs re-attaching.
 */
export function applySkynetLocale(win: Window, doc: Document, settings: SkynetSettings, previous: SkynetLocale): boolean {
  const skynet = win as unknown as SkynetWindow;
  const widget = skynet.$chatWidget;

  /* widget.js reads this in its own onload, so a change that lands while it is still loading
     is picked up there with nothing else to do. */
  skynet.$chatWidgetSettings = settings;

  if (widget == null) {
    return false;
  }

  const createWidget = skynet.CustomerChatWidgetBundle?.createCustomerChatWidgetSdk;

  /* Same country, or no way to build a replacement: switch the language in place. */
  if (settings.country === previous?.country || typeof createWidget !== 'function' || typeof widget.destroy !== 'function') {
    setLanguage(widget, settings.language);

    return false;
  }

  /* ⚠️ THE REPLACEMENT IS BUILT AND STARTED BEFORE THE RUNNING WIDGET IS DESTROYED. A
     destroyed widget can never start again, so anything that fails after destroy() leaves
     the page with no chat at all. Building one is inert (widget.js itself builds one at
     load), and init() mounts SYNCHRONOUSLY and returns nothing: a session or network failure
     after that is handled inside the widget, exactly as on a first page load. So "did it
     start" is answerable right here — it threw, or it put no host in the page. Either way
     the new one is torn down and the old one keeps running, with the language still
     switched. Both widgets exist only within this call, so the browser never paints two. */
  const hostsBefore = new Set(Array.from(doc.querySelectorAll('.ccw-host')));
  let fresh: ChatWidget = null;
  let started = false;

  try {
    fresh = createWidget();

    if (fresh != null && typeof fresh.init === 'function') {
      fresh.init(settings);
      started = Array.from(doc.querySelectorAll('.ccw-host'))
        .some(host => !hostsBefore.has(host) && host.childElementCount > 0);
    }
  } catch {
    /* A failure to build is a failure to move, nothing more — handled just below. */
  }

  if (!started) {
    if (fresh != null) {
      try {
        fresh.destroy?.();
      } catch {
        /* Tearing down something that never started; nothing to report. */
      }
    }

    removeEmptyHosts(doc);
    setLanguage(widget, settings.language);

    return false;
  }

  /* A pending setUser poll would land on the destroyed instance; the caller re-attaches. */
  doc.getElementById(SKYNET_SET_USER_SCRIPT_ID)?.remove();

  try {
    widget.destroy();
  } catch {
    /* The replacement is already mounted; a noisy teardown changes nothing on screen. */
  }

  removeEmptyHosts(doc);

  skynet.$chatWidget = skynet.CustomerChatWidget = fresh;

  /* The widget prefers its own saved language over settings.language. */
  setLanguage(fresh, settings.language);

  return true;
}

/** destroy() empties its host but leaves the element in the page. */
function removeEmptyHosts(doc: Document): void {
  doc.querySelectorAll('.ccw-host:empty').forEach(host => host.remove());
}

/** The widget's in-place switch; a failure leaves it on the language it had. */
function setLanguage(widget: ChatWidget, language: string): void {
  try {
    Promise.resolve(widget.setLanguage?.(language)).catch(() => undefined);
  } catch {
    /* An absent or throwing setLanguage is not worth breaking a locale change over. */
  }
}
